import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Customer, Device, Lookup, PagedResults } from '../app.types';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { ModalComponent } from '../core/modal/modal.component';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DeviceManagementService } from '../services/device-management/device-management.service';
import { DialogService } from '../services/dialog/dialog.service';
import { LookupService } from '../services/lookup/lookup.service';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import { clearControlsForErrorDetails, isFormValid } from '../core/form-helpers';
import { createDefaultData } from '../confirm-dialog/confirm-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpErrorService } from '../services/http-error.service';

@Component({
    selector: 'bulk-edit-device-modal',
    templateUrl: './bulk-edit-device-modal.component.html',
    styleUrls: ['./bulk-edit-device-modal.component.scss'],
    standalone: false
})
export class BulkEditDeviceModalComponent implements OnInit, OnDestroy {
  @Input() devices: Device[];
  @Input() triggerRefresh: Subject<null>;
  @Input() customers: Customer[];
  @Output() close: EventEmitter<boolean> = new EventEmitter();
  
  @ViewChild(ModalComponent) modal: ModalComponent;
  
  assetTypes: Lookup[] = [];
  showMixedCustomerOption = false;
  showMixedAssetTypeOption = false;

  customerOptions: { key: string; value: string }[] = [];
  assetTypeOptions: { key: string; value: string }[] = [];

  form: FormGroup;
  initialMixedEnabled: boolean;
  initialMixedRecordingMap: boolean;
  initialMixedCameraSnap: boolean;
  showMixedEnabled: FormControl = new FormControl(true);
  showMixedRecordingMap: FormControl = new FormControl(true);
  showMixedCameraSnap: FormControl = new FormControl(true);
  
  requestInProgress = false;

  addingAssetType = false;

  refreshAssetTypes$ = new Subject<string>();
  destroy$ = new Subject();

  constructor(
      private formBuilder: FormBuilder,
      private deviceManagementService: DeviceManagementService,
      private readonly dialogService: DialogService,
      private lookupService: LookupService,
      private readonly httpErrorService: HttpErrorService
    ) {
  }
  
  modalCleanup() {
    // if a request was triggered, emit true to trigger a refresh of device table
    this.close.emit(this.requestInProgress);
    this.requestInProgress = false;
  }
  
  ngOnInit(): void {
    const distinctCustomers = [...new Set(this.devices.map(x => x.customer?.id))];
    const distinctAssetTypes = [...new Set(this.devices.map(x => x.assetType?.id))];
    this.showMixedCustomerOption = distinctCustomers.length > 1;
    this.showMixedAssetTypeOption = distinctAssetTypes.length > 1;

    this.customerOptions = [
      ...(this.showMixedCustomerOption ? [{ key: '-1', value: 'Mixed' }] : []),
      ...(this.customers ?? []).map(x => ({ key: x.id, value: x.name }))
    ];

    const distinctEnabled = [...new Set(this.devices.map(x => x.enabled))];
    const distinctEnabledRecMap = [...new Set(this.devices.map(x => x.enableRecordingMapRequest))];
    const distinctEnabledCamSnap = [...new Set(this.devices.map(x => x.enableCamerasSnapshotRequest))];
    
    if(distinctEnabled.length === 1){
      this.showMixedEnabled.disable();
      this.showMixedEnabled.setValue(false);
    } 
    if(distinctEnabledRecMap.length === 1){
      this.showMixedRecordingMap.disable();
      this.showMixedRecordingMap.setValue(false);
    } 
    if(distinctEnabledCamSnap.length === 1){
      this.showMixedCameraSnap.disable();
      this.showMixedCameraSnap.setValue(false);
    } 

    this.form = this.formBuilder.group({
        customerId: [this.showMixedCustomerOption ? "-1" : distinctCustomers[0] ?? this.customers[0]?.id, Validators.required],
        assetTypeId: [this.showMixedAssetTypeOption ? "-1" : distinctAssetTypes[0] ?? "0"],
        apiKey: [''],
        enabled: this.formBuilder.group(
          {
            mixed: this.showMixedEnabled, 
            value: new FormControl(distinctEnabled[0])
          }),
        enableRecordingMapRequest: this.formBuilder.group(
          {
            mixed: this.showMixedRecordingMap, 
            value: new FormControl(distinctEnabledRecMap[0])
          }),
        enableCamerasSnapshotRequest: this.formBuilder.group(
          {
            mixed: this.showMixedCameraSnap, 
            value: new FormControl(distinctEnabledCamSnap[0])
          })
      });
      
    this.refreshAssetTypes$.pipe(
      startWith(''),
      switchMap(x => {
        const assetTypes$: Observable<PagedResults<Lookup>> = this.lookupService.getList('AssetType', {});
        return combineLatest([assetTypes$, of(x)])
      }),
      takeUntil(this.destroy$)
    ).subscribe(([data, newAssetTypeID]) => {
      this.assetTypes = data.results;
      this.assetTypeOptions = [
        { key: null, value: 'None' },
        ...(this.showMixedAssetTypeOption ? [{ key: '-1', value: 'Mixed' }] : []),
        ...this.assetTypes.map(x => ({ key: x.id, value: x.name }))
      ];
      if(newAssetTypeID) {
        this.form.controls['assetTypeId'].setValue(newAssetTypeID);
      }
      if(!this.assetTypes.some(x => x.id === this.form.controls['assetTypeId'].value) 
        && this.form.controls['assetTypeId'].value !== "-1") {
        this.form.controls['assetTypeId'].setValue(null);
      }
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  submitForm(): void {
    if (!isFormValid(this.form)) {return;}

    const formData = this.form.getRawValue();
    this.requestInProgress = true;

    const updatedDevices: Device[] = [];
    const apiKey = formData.apiKey?.trim();
    const apiKeyUpdated = !!apiKey;
    const customerUpdated = formData.customerId !== "-1";

    for (const device of this.devices) {
      const { logsRequest: _, ...updatedDevice} = device;
      updatedDevice.apiKey = apiKey || device.apiKey;
      updatedDevice.customerId = formData.customerId === "-1" ? device.customer?.id : formData.customerId;
      updatedDevice.assetTypeId = formData.assetTypeId === "-1" ? device.assetType?.id : formData.assetTypeId ?? null;
      updatedDevice.enabled = formData.enabled.mixed ? device.enabled : formData.enabled.value;
      updatedDevice.enableRecordingMapRequest = formData.enableRecordingMapRequest.mixed ? device.enableRecordingMapRequest 
        : formData.enableRecordingMapRequest.value;
      updatedDevice.enableCamerasSnapshotRequest = formData.enableCamerasSnapshotRequest.mixed ? device.enableCamerasSnapshotRequest 
        : formData.enableCamerasSnapshotRequest.value;

      updatedDevices.push(updatedDevice);
    }

    const updateConfirmText = 'Are you sure you wish to update devices?';
    
    this.dialogService.confirm(createDefaultData(updateConfirmText)).subscribe((result) => {
      console.log(`Dialog result: ${result}`);

      if (!result){
        console.log("don't update devices ");
        this.requestInProgress = false;
      }

      if (result == true) {
        console.log('update devices ');

        this.deviceManagementService.updateDevices({ devices: updatedDevices, apiKeyUpdated, customerUpdated })
          .subscribe((res) => {
            if (res instanceof HttpErrorResponse) {
              clearControlsForErrorDetails(this.form, this.httpErrorService.getErrorDetails(res));
              this.requestInProgress = false;
              return;
            }

            if (res != null) {
              this.triggerRefresh.next(null);
              this.modal.closeModal();
            }
          });
      }
    });
  }

  openAssetTypeExpander(){
    this.addingAssetType = true;
  }

  assetTypeExpanderClosed(assetTypeId: string) {
    this.addingAssetType = false;

    this.refreshAssetTypes$.next(assetTypeId);
  }

  assetTypeDeleted() {
    this.refreshAssetTypes$.next(null);
  }

  updateMixedBooleanFromMixed(mixed: boolean, groupName: string){
    this.form.get(groupName).get('value').setValue(!mixed);
  }

  updateMixedBooleanFromValue(groupName: string){
    if(this.form.get(groupName).get('mixed').value){
      this.form.get(groupName).get('mixed').setValue(false);
      this.form.get(groupName).get('value').setValue(true);
    }
  }
}
